export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

const DEFAULTS: Required<ImageCompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
};

function shouldCompress(file: File): boolean {
  if (!file.type.startsWith('image/')) return false;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return false;
  return true;
}

async function fileToImageBitmap(file: File): Promise<ImageBitmap> {
  return createImageBitmap(file);
}

async function canvasToFile(canvas: HTMLCanvasElement, type: string, quality: number, originalName: string): Promise<File> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  if (!blob) throw new Error('No se pudo comprimir la imagen');

  const ext = type === 'image/webp' ? 'webp' : 'jpg';
  const safeName = originalName.replace(/\.[^/.]+$/, '');
  return new File([blob], `${safeName}.${ext}`, { type, lastModified: Date.now() });
}

export async function compressImageFile(file: File, options?: ImageCompressionOptions): Promise<File> {
  if (!shouldCompress(file)) return file;

  const { maxWidth, maxHeight, quality } = { ...DEFAULTS, ...(options || {}) };

  try {
    const bitmap = await fileToImageBitmap(file);

    const ratio = Math.min(maxWidth / bitmap.width, maxHeight / bitmap.height, 1);
    const targetWidth = Math.max(1, Math.round(bitmap.width * ratio));
    const targetHeight = Math.max(1, Math.round(bitmap.height * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const compressed = await canvasToFile(canvas, 'image/jpeg', quality, file.name);

    // Keep original if compression made it larger
    if (compressed.size >= file.size) return file;

    return compressed;
  } catch {
    return file;
  }
}

export async function compressImages(files: File[], options?: ImageCompressionOptions): Promise<File[]> {
  return Promise.all(files.map((file) => compressImageFile(file, options)));
}
